import { AlertTriangle, TrendingUp } from 'lucide-react'
import type { UsageLimitKey } from '@/lib/types/subscription'
import { Progress } from '@/components/ui/progress'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { USAGE_LIMITS } from '@/lib/types/subscription'
import { useUsageLimitCheck } from '@/lib/db/rxdb/hooks/entities/subscriptions'

interface UsageMetricItemProps {
  limitKey: UsageLimitKey
  label: string
}

function UsageMetricItem({ limitKey, label }: UsageMetricItemProps) {
  const usage = useUsageLimitCheck(limitKey)

  const formatNumber = (num: number) => {
    if (num === -1) return 'Unlimited'
    if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}GB`
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}MB`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  if (usage.isUnlimited) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{label}</span>
          <span className="text-muted-foreground">
            {formatNumber(usage.currentUsage)} / Unlimited
          </span>
        </div>
        <Progress value={0} className="h-2 bg-primary/10" />
      </div>
    )
  }

  const isNearLimit = usage.percentUsed >= 80
  const isOverLimit = usage.isOverLimit

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <div className="flex items-center gap-2">
          {isNearLimit && !isOverLimit && (
            <AlertTriangle className="size-3.5 text-yellow-600 dark:text-yellow-400" />
          )}
          {isOverLimit && (
            <AlertTriangle className="size-3.5 text-destructive" />
          )}
          <span
            className={
              isOverLimit
                ? 'text-destructive font-medium'
                : isNearLimit
                  ? 'text-yellow-600 dark:text-yellow-500'
                  : 'text-muted-foreground'
            }
          >
            {formatNumber(usage.currentUsage)} / {formatNumber(usage.limit)}
          </span>
        </div>
      </div>
      <Progress
        value={Math.min(usage.percentUsed, 100)}
        className="h-2"
        indicatorClassName={
          isOverLimit
            ? 'bg-destructive'
            : isNearLimit
              ? 'bg-yellow-500'
              : undefined
        }
      />
      {isNearLimit && (
        <p className="text-xs text-muted-foreground">
          {isOverLimit
            ? 'Limit exceeded - upgrade to continue'
            : `${usage.remaining} remaining`}
        </p>
      )}
    </div>
  )
}

interface UsageMetricsProps {
  className?: string
  variant?: 'card' | 'inline'
  showTitle?: boolean
  showUpgrade?: boolean
}

/**
 * UsageMetrics component to display current usage vs plan limits
 *
 * @example
 * ```tsx
 * <UsageMetrics variant="card" showUpgrade />
 * ```
 */
export function UsageMetrics({
  className,
  variant = 'card',
  showTitle = true,
  showUpgrade = true,
}: UsageMetricsProps) {
  // Render the 6 quota dimensions the Phase 0 backend tracks. Storage shows
  // the 100MB / unlimited cap; the per-file cap (maxFileSizeBytes) is enforced
  // at upload time and surfaces via a different UI affordance, so it's not
  // shown as a progress bar here.
  const tasksUsage = useUsageLimitCheck(USAGE_LIMITS.TASKS)
  const storageUsage = useUsageLimitCheck(USAGE_LIMITS.STORAGE)
  const membersUsage = useUsageLimitCheck(USAGE_LIMITS.MEMBERS)
  const projectsUsage = useUsageLimitCheck(USAGE_LIMITS.PROJECTS)
  // Guests + workspaces are rendered as full bars below but don't currently
  // contribute to the "show upgrade banner" condition (those are downgrade-blockers,
  // not soft-warn dimensions).

  const hasWarnings =
    tasksUsage.isNearingLimit ||
    tasksUsage.isOverLimit ||
    storageUsage.isNearingLimit ||
    storageUsage.isOverLimit ||
    membersUsage.isNearingLimit ||
    membersUsage.isOverLimit ||
    projectsUsage.isNearingLimit ||
    projectsUsage.isOverLimit

  const handleUpgrade = () => {
    window.location.href = '/settings/subscription'
  }

  const metricsContent = (
    <div className="space-y-4">
      <UsageMetricItem limitKey={USAGE_LIMITS.MEMBERS} label="Members" />
      <UsageMetricItem limitKey={USAGE_LIMITS.GUESTS} label="Guests" />
      <UsageMetricItem limitKey={USAGE_LIMITS.TASKS} label="Tasks" />
      <UsageMetricItem limitKey={USAGE_LIMITS.PROJECTS} label="Projects" />
      <UsageMetricItem limitKey={USAGE_LIMITS.STORAGE} label="Storage" />
      <UsageMetricItem limitKey={USAGE_LIMITS.WORKSPACES} label="Workspaces" />

      {hasWarnings && showUpgrade && (
        <div className="pt-2">
          <Button onClick={handleUpgrade} className="w-full" size="sm">
            <TrendingUp className="size-4" />
            Upgrade for More
          </Button>
        </div>
      )}
    </div>
  )

  if (variant === 'inline') {
    return (
      <div className={className}>
        {showTitle && (
          <h3 className="font-semibold text-sm mb-4">Usage & Limits</h3>
        )}
        {metricsContent}
      </div>
    )
  }

  return (
    <Card className={className}>
      {showTitle && (
        <CardHeader>
          <CardTitle>Usage & Limits</CardTitle>
          <CardDescription>
            Track your usage against your plan limits
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className={showTitle ? '' : 'pt-6'}>
        {metricsContent}
      </CardContent>
    </Card>
  )
}
